/**
 * LCFPIBulkUpload.js
 * LCFPI Intake Template v2.0 Upload Wizard
 * 5-step: Upload → Map → Validate → Import → Workflow
 *
 * Template: 5 sections, 28 columns
 * Backward-compatible with legacy templates
 * Client-side parse via xlsx library (already installed)
 */
import React, { useState, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import client from "@/api/client";
import {
  X, Upload, CheckCircle2, AlertCircle, Loader, ChevronRight,
  ChevronLeft, FileText, Play, Download, BarChart2, Leaf,
  DollarSign, Zap,
} from "lucide-react";
import { toast } from "sonner";

// ── Template definition ──────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: "A", label: "Project Information", color: "#0a1628",
    fields: [
      { key: "org_name",           label: "Organisation Name *",                   required: true  },
      { key: "project_name",       label: "Project Name",                          required: false },
      { key: "sector",             label: "Sector *",                              required: true  },
      { key: "description",        label: "Project Description",                   required: false },
      { key: "state",              label: "State",                                 required: false },
      { key: "lga",                label: "LGA *",                                 required: true  },
      { key: "project_location",   label: "Project Location",                      required: false },
      { key: "implementation_stage",label: "Implementation Stage",                 required: false },
      { key: "funding_need_usd",   label: "Funding Need (USD) *",                  required: true  },
      { key: "contact_person",     label: "Contact Person",                        required: false },
      { key: "contact_email",      label: "Contact Email",                         required: false },
      { key: "contact_phone",      label: "Contact Phone",                         required: false },
    ],
  },
  {
    id: "B", label: "Climate Screening", color: "#064e3b",
    fields: [
      { key: "current_energy_source",             label: "Current Energy Source",                required: false },
      { key: "annual_electricity_kwh",            label: "Annual Electricity Consumption (kWh/year)", required: false },
      { key: "annual_diesel_litres",              label: "Annual Diesel Consumption (Litres/year)", required: false },
      { key: "expected_climate_benefit",          label: "Expected Climate Benefit",             required: false },
      { key: "estimated_annual_reduction_tco2e",  label: "Estimated Annual Emission Reduction (tCO2e)", required: false },
    ],
  },
  {
    id: "C", label: "Circularity Screening", color: "#1e3a5f",
    fields: [
      { key: "annual_waste_tonnes",         label: "Annual Waste Generated (Tonnes/year)", required: false },
      { key: "waste_disposal_method",       label: "Current Waste Disposal Method",        required: false },
      { key: "circular_economy_opportunity",label: "Circular Economy Opportunity",         required: false },
      { key: "main_material_recovered",     label: "Main Material Recovered",              required: false },
    ],
  },
  {
    id: "D", label: "Finance Readiness (Yes/No)", color: "#2d1b69",
    fields: [
      { key: "feasibility_study",      label: "Feasibility Study (Yes/No)",        required: false },
      { key: "financial_model",        label: "Financial Model (Yes/No)",          required: false },
      { key: "environmental_approval", label: "Environmental Approval / ESIA (Yes/No)", required: false },
      { key: "permits_land_secured",   label: "Permits / Land Secured (Yes/No)",  required: false },
    ],
  },
  {
    id: "E", label: "Carbon Screening (Yes/No)", color: "#7c2d12",
    fields: [
      { key: "carbon_credit_interest",   label: "Carbon Credit Interest (Yes/No)",    required: false },
      { key: "existing_baseline_data",   label: "Existing Baseline Data (Yes/No)",    required: false },
      { key: "monitoring_data_available",label: "Monitoring Data Available (Yes/No)", required: false },
    ],
  },
];

const ALL_FIELDS = SECTIONS.flatMap(s => s.fields);
const SECTOR_OPTIONS = ["Renewable Energy","Waste Management","Agriculture","Transport","Housing","Manufacturing","Water","Health","Other"];
const LGA_OPTIONS    = ["Agege","Alimosho","Apapa","Badagry","Epe","Eti-Osa","Ibeju-Lekki","Ifako-Ijaiye","Ikeja","Ikorodu","Ikoyi","Kosofe","Lagos Island","Lagos Mainland","Mushin","Ojo","Oshodi-Isolo","Shomolu","Surulere"];
const STAGE_OPTIONS  = ["Concept","Planning","Design","Construction","Operational","Expansion"];

const STEPS = [
  { id: 1, label: "Upload",   icon: Upload      },
  { id: 2, label: "Map",      icon: FileText     },
  { id: 3, label: "Validate", icon: CheckCircle2 },
  { id: 4, label: "Import",   icon: Play         },
  { id: 5, label: "Complete", icon: CheckCircle2 },
];

// ── Auto-map column headers to field keys ────────────────────────────────────
function autoMap(headers) {
  const mapping = {};
  const normalize = s => s.toLowerCase().replace(/[\s_\-*()/]/g,"");
  const matchers = {
    org_name:                    ["orgname","organisation","organization","companyname","company"],
    project_name:                ["projectname","project","name","title"],
    sector:                      ["sector","industry","type"],
    description:                 ["description","projectdescription","desc","summary"],
    state:                       ["state"],
    lga:                         ["lga","localgovernment","location","area"],
    project_location:            ["projectlocation","location","address","site"],
    implementation_stage:        ["implementationstage","stage","phase","status"],
    funding_need_usd:            ["fundingneed","fundingneedusd","funding","ticketsize","amount"],
    contact_person:              ["contactperson","contact","person","ceo"],
    contact_email:               ["contactemail","email","emailaddress"],
    contact_phone:               ["contactphone","phone","mobile","telephone"],
    current_energy_source:       ["energysource","currentsource","powersource"],
    annual_electricity_kwh:      ["electricitykwh","electricity","electricityconsumption"],
    annual_diesel_litres:        ["dieselconsumption","diesel","diesellitres"],
    expected_climate_benefit:    ["climatebenefit","climateopportunity","ghgbenefit"],
    estimated_annual_reduction_tco2e: ["emissionreduction","co2reduction","ghgreduction","annualreduction"],
    annual_waste_tonnes:         ["wastegenerated","annualwaste","wastevolume"],
    waste_disposal_method:       ["disposalmethod","wastedisposal","currentdisposal"],
    circular_economy_opportunity:["circularopportunity","circularity","circulareconomy"],
    main_material_recovered:     ["materialrecovered","mainmaterial","materials"],
    feasibility_study:           ["feasibilitystudy","feasibility"],
    financial_model:             ["financialmodel","financialstatements"],
    environmental_approval:      ["environmentalapproval","esia","environmentalassessment"],
    permits_land_secured:        ["permits","landsecured","permitsland"],
    carbon_credit_interest:      ["carboncreditinterest","carboninterest","credits"],
    existing_baseline_data:      ["baselinedata","existingbaseline"],
    monitoring_data_available:   ["monitoringdata","monitoring"],
  };
  for (const h of headers) {
    const n = normalize(h);
    for (const [field, patterns] of Object.entries(matchers)) {
      if (patterns.some(p => n.includes(p) || p.includes(n.substring(0,8)))) {
        if (!mapping[field]) mapping[field] = h;
      }
    }
  }
  return mapping;
}

// ── Validate a single row ────────────────────────────────────────────────────
function validateRow(row, mapping, rowNum) {
  const errors = [], warnings = [], mapped = {};
  for (const [field, col] of Object.entries(mapping)) {
    if (col && row[col] !== undefined && row[col] !== "") {
      mapped[field] = row[col];
    }
  }
  // Required field checks
  if (!mapped.org_name || !String(mapped.org_name).trim()) {
    errors.push("Missing Organisation Name");
  }
  if (!mapped.funding_need_usd && mapped.funding_need_usd !== 0) {
    warnings.push("Funding Need not specified");
  }
  if (!mapped.lga) warnings.push("LGA not specified");

  // Normalize sector
  if (mapped.sector) {
    const s = String(mapped.sector).toLowerCase().replace(/\s+/g,"_");
    const valid = ["renewable_energy","waste_management","agriculture","transport","housing","manufacturing","water","health","other"];
    mapped.sector = valid.includes(s) ? s : "other";
    if (!valid.includes(s)) warnings.push(`Sector "${mapped.sector}" not recognised — mapped to "Other"`);
  }
  // Parse numbers
  if (mapped.funding_need_usd) {
    const n = Number(String(mapped.funding_need_usd).replace(/[,$]/g,""));
    if (isNaN(n)) warnings.push("Funding Need is not a number — will use 0");
    else mapped.funding_need_usd = n;
  }
  for (const f of ["annual_electricity_kwh","annual_diesel_litres","annual_waste_tonnes","estimated_annual_reduction_tco2e"]) {
    if (mapped[f]) {
      const n = Number(String(mapped[f]).replace(/[,$]/g,""));
      if (!isNaN(n)) mapped[f] = n;
    }
  }
  return { row: rowNum, raw: row, mapped, errors, warnings, valid: errors.length === 0 };
}

// ── Generate LCFPI Intake Template v2.0 ─────────────────────────────────────
function downloadTemplate() {
  const wb = XLSX.utils.book_new();

  // ── Instructions sheet ────────────────────────────────────────────────────
  const instrData = [
    ["LCFPI Project Intake Template v2.0"],
    ["Lagos Climate Finance Pipeline Initiative — DeCarbonIQ Platform"],
    [""],
    ["INSTRUCTIONS"],
    ["1. Complete the 'Projects' sheet. Do not modify column headers."],
    ["2. Columns marked with * are required. All other columns are optional."],
    ["3. Use Yes or No for all Yes/No columns."],
    ["4. Upload the completed file through the LCFPI platform."],
    ["5. The platform will run Climate, Circularity, Finance Readiness and Carbon Monetisation assessments automatically."],
    ["6. Maximum 200 projects per upload."],
    [""],
    ["FIELD DESCRIPTIONS"],
    ["Field", "Description", "Required", "Format"],
    ...ALL_FIELDS.map(f => [
      f.label.replace(" *",""),
      f.required ? "Required field" : "Optional — improves assessment quality",
      f.required ? "Yes" : "No",
      f.key.includes("kwh") || f.key.includes("usd") || f.key.includes("litres") || f.key.includes("tonnes") ? "Number" :
      f.key.includes("email") ? "Email address" :
      f.key.includes("phone") ? "Phone number" :
      f.key === "sector" ? "See dropdown: " + SECTOR_OPTIONS.join(", ") :
      f.key === "lga" ? "See dropdown: " + LGA_OPTIONS.join(", ") :
      f.key === "implementation_stage" ? "See dropdown: " + STAGE_OPTIONS.join(", ") :
      f.key.endsWith("_study") || f.key.endsWith("_model") || f.key.endsWith("_approval") ||
      f.key.endsWith("_secured") || f.key.endsWith("_interest") || f.key.endsWith("_data") ||
      f.key.endsWith("_available") ? "Yes or No" : "Text"
    ]),
  ];
  const wsInstr = XLSX.utils.aoa_to_sheet(instrData);
  wsInstr["!cols"] = [{wch:35},{wch:55},{wch:12},{wch:50}];
  XLSX.utils.book_append_sheet(wb, wsInstr, "Instructions");

  // ── Projects sheet ────────────────────────────────────────────────────────
  // Row 1: Section labels
  // Row 2: Column headers
  // Row 3: Sample project
  const sectionRow = [];
  const headerRow  = [];
  const sampleRow  = [];
  for (const section of SECTIONS) {
    for (const field of section.fields) {
      sectionRow.push(`Section ${section.id}: ${section.label}`);
      headerRow.push(field.label);
    }
  }
  // Sample project (Lagos biogas example)
  const sampleValues = {
    org_name: "Lagos Clean Energy Co. Ltd", project_name: "Agege Biogas Project",
    sector: "Renewable Energy", description: "Biogas from organic waste for electricity generation",
    state: "Lagos", lga: "Agege", project_location: "Oko-Oba Industrial Estate, Agege",
    implementation_stage: "Planning", funding_need_usd: 500000,
    contact_person: "Amaka Okafor", contact_email: "amaka@lagoscleaneergy.com",
    contact_phone: "+234 801 234 5678",
    current_energy_source: "Diesel Generator",
    annual_electricity_kwh: 500000, annual_diesel_litres: 60000,
    expected_climate_benefit: "Methane capture and renewable electricity generation",
    estimated_annual_reduction_tco2e: 800,
    annual_waste_tonnes: 3000, waste_disposal_method: "Open dump",
    circular_economy_opportunity: "Biogas from organic waste; digestate as biofertilizer",
    main_material_recovered: "Biogas, Digestate",
    feasibility_study: "Yes", financial_model: "No",
    environmental_approval: "No", permits_land_secured: "No",
    carbon_credit_interest: "Yes", existing_baseline_data: "No",
    monitoring_data_available: "No",
  };
  ALL_FIELDS.forEach(f => sampleRow.push(sampleValues[f.key] ?? ""));

  const wsData = XLSX.utils.aoa_to_sheet([sectionRow, headerRow, sampleRow]);
  wsData["!cols"] = ALL_FIELDS.map(() => ({ wch: 28 }));

  // Freeze rows 1 and 2
  wsData["!freeze"] = { xSplit: 0, ySplit: 2 };

  XLSX.utils.book_append_sheet(wb, wsData, "Projects");
  XLSX.writeFile(wb, "LCFPI_Intake_Template_v2.0.xlsx");
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export function BulkUploadWizard({ onClose, onImported }) {
  const [step,      setStep]      = useState(1);
  const [fileData,  setFileData]  = useState(null);
  const [mapping,   setMapping]   = useState({});
  const [validated, setValidated] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result,    setResult]    = useState(null);
  const [dragOver,  setDragOver]  = useState(false);
  const fileRef = useRef(null);

  const parseFile = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb   = XLSX.read(data, { type: "array" });
        // Find the "Projects" sheet or fall back to first sheet
        const sheetName = wb.SheetNames.find(n => n.toLowerCase().includes("project")) || wb.SheetNames[0];
        const sheet = wb.Sheets[sheetName];
        // Read as rows — skip row 1 if it's a section-label row
        const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        if (rawRows.length < 2) { toast.error("Sheet appears empty"); return; }

        // Detect if first row is section labels or column headers
        // Section labels contain "Section A:" prefix; headers are field names
        const firstRow = rawRows[0].map(String);
        let headers, dataStart;
        if (firstRow.some(v => v.startsWith("Section "))) {
          // v2.0 template — row 1 = section labels, row 2 = headers, row 3+ = data
          headers   = rawRows[1].map(String).filter(Boolean);
          dataStart = 2;
        } else {
          headers   = firstRow.filter(Boolean);
          dataStart = 1;
        }

        const dataRows = rawRows.slice(dataStart)
          .filter(row => row.some(cell => cell !== ""))
          .map(row => Object.fromEntries(headers.map((h, i) => [h, row[i] ?? ""])));

        setFileData({ headers, rows: dataRows, fileName: file.name, sheetName, template_version: firstRow.some(v=>v.startsWith("Section ")) ? "v2.0" : "legacy" });
        setMapping(autoMap(headers));
        setStep(2);
      } catch (err) {
        toast.error("Could not parse file — ensure it is .xlsx or .csv");
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) parseFile(f); };
  const handleFile = (e) => { const f = e.target.files[0]; if (f) parseFile(f); };

  const runValidation = () => {
    const results = fileData.rows.map((row, i) => validateRow(row, mapping, i + 2));
    setValidated(results);
    setStep(3);
  };

  const runImport = async () => {
    setImporting(true);
    const valid = validated.filter(r => r.valid).map(r => r.mapped);
    try {
      const r = await client.post("/lcfpi/bulk-upload", { projects: valid, trigger_assessment: true });
      setResult(r.data);
      setStep(5);
      onImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const validCount   = validated.filter(r => r.valid).length;
  const invalidCount = validated.filter(r => !r.valid).length;
  const warnCount    = validated.filter(r => r.warnings.length > 0).length;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end"
      style={{ background: "#000000cc" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="h-full flex flex-col" data-testid="bulk-upload-wizard"
        style={{ width: "min(660px,100vw)", background: "#060d1a", borderLeft: "1px solid #1e293b" }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 flex-shrink-0">
          <div>
            <p className="text-sm font-black text-white flex items-center gap-2">
              <Upload size={14} color="#60a5fa" /> Upload Projects
            </p>
            <p className="text-[10px] text-slate-500 mt-0.5">
              LCFPI Intake Template v2.0 &nbsp;&middot;&nbsp; Up to 200 projects
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        {/* Step bar */}
        <div className="flex px-6 py-3 border-b border-slate-800 gap-1 flex-shrink-0">
          {STEPS.map(s => {
            const done = s.id < step, active = s.id === step;
            return (
              <div key={s.id} className="flex-1 flex flex-col items-center gap-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all
                  ${done?"bg-green-600":active?"bg-blue-600":"bg-slate-800"}`}>
                  {done ? <CheckCircle2 size={12} color="#fff" /> : <s.icon size={11} color={active?"#fff":"#64748b"} />}
                </div>
                <p className={`text-[9px] font-semibold ${active?"text-blue-400":done?"text-green-400":"text-slate-600"}`}>
                  {s.label}
                </p>
              </div>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Description */}
              <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
                <p className="text-xs text-slate-300 leading-relaxed mb-3">
                  Upload projects using the LCFPI Intake Template. The platform will automatically create
                  each project and guide you through Climate, Circularity, Climate Finance and Carbon
                  Monetisation assessments.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {SECTIONS.map(s => (
                    <div key={s.id} className="flex items-center gap-2 text-[10px] text-slate-400">
                      <CheckCircle2 size={10} className="text-green-400 flex-shrink-0" />
                      Section {s.id}: {s.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Drop zone */}
              <div
                onDragOver={e=>{e.preventDefault();setDragOver(true);}}
                onDragLeave={()=>setDragOver(false)}
                onDrop={handleDrop}
                onClick={()=>fileRef.current?.click()}
                data-testid="bulk-upload-dropzone"
                className="border-2 border-dashed rounded-2xl flex flex-col items-center justify-center p-8 cursor-pointer transition-all"
                style={{ borderColor: dragOver?"#3b82f6":"#1e293b", background: dragOver?"#1e3a5f20":"transparent" }}>
                <Upload size={28} className="mb-3" color="#334155" />
                <p className="text-sm font-bold text-white mb-1">Drop your file here</p>
                <p className="text-xs text-slate-500">XLSX or CSV &nbsp;&middot;&nbsp; Max 200 projects</p>
                <input ref={fileRef} type="file" accept=".xlsx,.csv,.xls" onChange={handleFile} className="hidden" />
              </div>

              {/* Template download */}
              <div className="flex items-center justify-between bg-slate-800/30 border border-slate-700/30 rounded-xl px-4 py-3">
                <div>
                  <p className="text-xs font-bold text-white">LCFPI Intake Template v2.0</p>
                  <p className="text-[10px] text-slate-500">28 columns across 5 sections &nbsp;&middot;&nbsp; with sample project</p>
                </div>
                <button onClick={downloadTemplate} data-testid="bulk-download-template"
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                  style={{ background:"#1e293b", color:"#60a5fa", border:"1px solid #1e40af40" }}>
                  <Download size={11} /> Download
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Map */}
          {step === 2 && fileData && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText size={12} color="#60a5fa" />
                <p className="text-xs text-slate-300">
                  {fileData.fileName} &nbsp;&middot;&nbsp; {fileData.rows.length} rows &nbsp;&middot;&nbsp;
                  <span className="text-green-400">{fileData.template_version}</span>
                </p>
              </div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Map columns to fields</p>
              {SECTIONS.map(section => (
                <div key={section.id} className="rounded-xl border border-slate-700/40 overflow-hidden">
                  <div className="px-3 py-2" style={{ background: section.color }}>
                    <p className="text-[10px] font-bold text-white uppercase tracking-wider">
                      Section {section.id}: {section.label}
                    </p>
                  </div>
                  <div className="p-3 space-y-2">
                    {section.fields.map(f => (
                      <div key={f.key} className="flex items-center gap-3">
                        <span className="text-[10px] text-slate-300 font-semibold" style={{ width: 180 }}>
                          {f.label.replace(" *","")}
                          {f.required && <span className="text-red-400 ml-0.5">*</span>}
                        </span>
                        <select value={mapping[f.key] || ""} onChange={e=>setMapping(m=>({...m,[f.key]:e.target.value}))}
                          data-testid={`map-field-${f.key}`}
                          className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-[10px] text-white outline-none focus:border-blue-500">
                          <option value="">— Not mapped —</option>
                          {fileData.headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 3: Validate */}
          {step === 3 && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3 mb-2">
                <div className="bg-green-900/20 border border-green-700/30 rounded-xl p-3 text-center">
                  <p className="text-xl font-black text-green-400">{validCount}</p>
                  <p className="text-xs text-slate-400">Ready</p>
                </div>
                <div className="bg-amber-900/20 border border-amber-700/30 rounded-xl p-3 text-center">
                  <p className="text-xl font-black text-amber-400">{warnCount}</p>
                  <p className="text-xs text-slate-400">Warnings</p>
                </div>
                <div className="bg-red-900/20 border border-red-700/30 rounded-xl p-3 text-center">
                  <p className="text-xl font-black text-red-400">{invalidCount}</p>
                  <p className="text-xs text-slate-400">Skipped</p>
                </div>
              </div>
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {validated.map(r => (
                  <div key={r.row} data-testid={`validate-row-${r.row}`}
                    className="flex items-start gap-3 px-3 py-2.5 rounded-xl"
                    style={{ background: r.errors.length>0?"#7f1d1d20":r.warnings.length>0?"#78350f20":"#14532d20" }}>
                    {r.errors.length>0 ? <AlertCircle size={11} className="text-red-400 flex-shrink-0 mt-0.5" /> :
                     r.warnings.length>0 ? <AlertCircle size={11} className="text-amber-400 flex-shrink-0 mt-0.5" /> :
                     <CheckCircle2 size={11} className="text-green-400 flex-shrink-0 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-white truncate">
                        Row {r.row}: {r.mapped.org_name || "(unnamed)"}
                      </p>
                      {[...r.errors,...r.warnings].slice(0,2).map((m,i) => (
                        <p key={i} className="text-[10px] text-slate-400">{m}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Importing */}
          {step === 4 && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              {importing ? (
                <>
                  <Loader size={32} className="animate-spin text-blue-400" />
                  <p className="text-sm font-bold text-white">Importing {validCount} projects…</p>
                  <p className="text-xs text-slate-500">Running assessment &amp; initialising workflow for each project</p>
                </>
              ) : <p className="text-sm text-slate-400">Ready to import</p>}
            </div>
          )}

          {/* Step 5: Complete */}
          {step === 5 && result && (
            <div className="space-y-4">
              <div className="flex flex-col items-center py-4 gap-3">
                <div className="w-14 h-14 rounded-full bg-green-900/30 border border-green-700/40 flex items-center justify-center">
                  <CheckCircle2 size={28} color="#22c55e" />
                </div>
                <p className="text-lg font-black text-white">Import Complete</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-900/20 border border-green-700/30 rounded-xl p-4 text-center">
                  <p className="text-2xl font-black text-green-400">{result.imported}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Projects Imported</p>
                </div>
                <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-4 text-center">
                  <p className="text-2xl font-black text-blue-400">{result.imported}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Workflows Initialised</p>
                </div>
              </div>
              <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl px-4 py-4 space-y-2">
                <p className="text-xs font-bold text-blue-300 uppercase tracking-wider mb-2">Next Steps</p>
                {[
                  { icon: BarChart2, label: "Climate Assessment",          desc: "Run climate impact calculations" },
                  { icon: Leaf,       label: "Circularity Assessment",     desc: "Quantify resource recovery" },
                  { icon: DollarSign, label: "Climate Finance Assessment", desc: "Score DFI readiness" },
                  { icon: Zap,        label: "Carbon Monetisation",        desc: "Identify carbon pathways" },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3">
                    <item.icon size={12} className="text-blue-400 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] font-bold text-white">{item.label}</p>
                      <p className="text-[9px] text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-600 text-center">
                Open any project in the Portfolio tab to begin assessments
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800 flex-shrink-0">
          <button onClick={() => step > 1 && step < 5 ? setStep(s=>s-1) : onClose()}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors">
            <ChevronLeft size={12} />
            {step === 1 || step === 5 ? "Cancel" : "Back"}
          </button>
          <div>
            {step === 2 && (
              <button onClick={runValidation} data-testid="bulk-validate-btn"
                className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-xl"
                style={{ background:"#1d4ed8", color:"#fff" }}>
                Validate <ChevronRight size={12}/>
              </button>
            )}
            {step === 3 && validCount > 0 && (
              <button onClick={()=>{setStep(4);setTimeout(runImport,200);}}
                data-testid="bulk-import-btn"
                className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-xl"
                style={{ background:"linear-gradient(135deg,#1d4ed8,#059669)", color:"#fff" }}>
                <Play size={11}/> Import {validCount} Projects
              </button>
            )}
            {step === 3 && validCount === 0 && (
              <p className="text-[10px] text-red-400">No valid rows — fix errors to proceed</p>
            )}
            {step === 5 && (
              <button onClick={onClose} data-testid="bulk-done-btn"
                className="text-xs font-semibold px-4 py-2 rounded-xl"
                style={{ background:"#1d4ed8", color:"#fff" }}>
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
